import { useEffect, useState } from 'react';

/** A scroll of MORE than this many pixels one way counts as a direction (a nudge of 8 px or less changes nothing). */
export const AWAY_SLOP_PX = 8;
/** Above this scroll position the page is at its top: nothing has scrolled under the thing yet. */
export const NEAR_TOP_PX = 80;
/** Within this many pixels of the last scroll position the page is at its end, where a page reserves room for it. */
export const NEAR_END_PX = 120;

/**
 * TRUE WHILE THE SHOPPER IS SCROLLING DOWN THROUGH THE PAGE (store polish, 10-28) — for a floating control that must
 * not sit over the content: on a phone the chat launcher covered product names in the shop grid.
 *
 * - A scroll DOWN of more than 8 px sets it.
 * - It clears only on a scroll UP of more than 8 px, or at either end of the page: the top (scrollY under 80) or the
 *   end (within 120 px of the last scroll position). Resting mid-page keeps it set, so the control never comes back
 *   over a card the shopper stopped on (fix round 1: the 600 ms idle return did exactly that).
 * - Each distance is measured from where the scroll turned (the lowest point while it is clear, the highest while it
 *   is set), so a long scroll up after a little more down still counts in full.
 * - The page is read once per animation frame, however many scroll events a phone sends (useScrollVisibility, owner
 *   10-25: reading the page inside every event made scrolling slow).
 * - The top's rubber band (iOS reports a negative scrollY while it bounces) reads as 0, and the end's reads as the
 *   end: neither is a scroll that sets it.
 * - `enabled: false` attaches nothing and answers false: the thing stays where it is (lite, 2026-11-03 — on an old phone
 *   a control that comes and goes with the finger was the owner's "showing hiding … was making the experience slower").
 */
export function useScrolledAway({ enabled = true } = {}) {
  const [away, setAway] = useState(false);

  useEffect(() => {
    if (!enabled) { setAway(false); return undefined; }
    if (typeof window === 'undefined') return undefined;
    const root = document.documentElement;
    const read = () => Math.max(0, window.scrollY || root.scrollTop || 0);
    const atAnEnd = (y) => y < NEAR_TOP_PX || (root.scrollHeight || 0) - (window.innerHeight || 0) - y <= NEAR_END_PX;
    let isAway = false;
    let turn = read();
    let frame = 0;
    const set = (next) => {
      if (next === isAway) return;
      isAway = next;
      setAway(next);
    };
    const decide = () => {
      frame = 0;
      const y = read();
      if (isAway) {
        if (atAnEnd(y) || turn - y > AWAY_SLOP_PX) { turn = y; set(false); }
        else if (y > turn) turn = y;
      } else if (y < turn) turn = y;
      else if (y - turn > AWAY_SLOP_PX && !atAnEnd(y)) { turn = y; set(true); }
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(decide);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled]);

  return away;
}

export default useScrolledAway;
